import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';

/** Requests that legitimately 401 as part of signing in. */
function isAuthEndpoint(url: string): boolean {
  return url.includes('/api/auth/');
}

/**
 * Turns an expired session into a clean trip back to the sign-in screen.
 *
 * `AuthService.isTokenExpired()` handles the case where the API told us when
 * the token dies. This is the backstop for everything else — a token revoked
 * server-side, a password changed on another device, or no expiry returned at
 * all. Without it a 401 surfaces as a generic per-screen error and the user
 * has no way to recover, because nothing in a native app corresponds to
 * "reload the page".
 *
 * There is no refresh-token endpoint, so re-authentication genuinely means
 * signing in again (see IONIC_MIGRATION_PLAN.md, backend backlog — adding a
 * refresh flow is the fix, and it is backend work).
 */
export const sessionExpiryInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const toast = inject(ToastService);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }

      // A failed login is a 401 and must show "wrong password", not
      // "your session expired".
      if (isAuthEndpoint(req.url)) {
        return throwError(() => err);
      }

      // Already signed out — a second 401 from an in-flight request must not
      // queue a second toast or a second navigation.
      if (!auth.accessToken) {
        return throwError(() => err);
      }

      auth.clearSession();
      toast.error('Your session has expired. Please sign in again.');
      void router.navigate(['/login']);

      return throwError(() => err);
    })
  );
};
